import styled from "styled-components";
import { getTheme, FlexStream, Stream, Text as Tx, type SizeVariant, type TypeVariant, type FontPalette, } from "@bracket/ui";

import type { FunctionComponent, } from "react";
import type { WEIString, } from "@bracket/types";




/**
 * Props for the BracketDollars component
 * @property {WEIString | bigint} amount - Amount in BRKT (WEI string or bigint)
 * @property {number} price - USD price of 1 BRKT
 * @property {Variant} [variant] - Typography variant to use
 * @property {number} [precision] - Number of decimal places to show for BRKT amount
 * @property {boolean} [label] - Whether to show "brktETH" label
 * @property {"reverse"} [order] - If "reverse", shows BRKT amount before USD value
 * @property {boolean} [multiline] - Whether to display values on multiple lines
 */
export declare type BracketDollarsProps = {
	amount: WEIString | bigint;
	price: number;
	variant?: TypeVariant;
	size?: SizeVariant;
	precision?: number;
	label?: boolean;
	order?: "reverse";
	multiline?: boolean;
	cents?: boolean;
	token?: string;
	hideBracket?: boolean;
	column?: boolean;
	font?: keyof FontPalette;
};



/**
 * BracketDollars component displays BRKT amounts with their USD value
 * @param amount - Amount in BRKT (WEI string or bigint)
 * @param price - USD price of 1 BRKT
 * @param variant - Typography variant to use
 * @param precision - Number of decimal places to show for BRKT amount
 * @param label - Whether to show "brktETH" label
 * @param order - If "reverse", shows BRKT amount before USD value
 * @param multiline - Whether to display values on multiple lines
 * @param cents - Whether to show cents in the USD value
 * @returns Formatted display of BRKT amount and USD value
 */
export const BracketDollars: FunctionComponent<BracketDollarsProps> = ({
	amount,
	price, 
	size,
	precision = 5,
	label,
	order,
	multiline,
	cents = true,
	hideBracket = false,
	column = false,
	font,
}) => {
	if(amount === undefined) return null;
	const bAmount = typeof amount === "bigint" ? amount : BigInt(Math.floor(Number(amount)));
	const iAmount = Number(bAmount) / 1e18;
	const brktValue = () => price <= 0 ? "0.00" : iAmount.toFixed(precision);

	const dollarValue = () => {
		return price.toLocaleString("en-US", {
			style: "currency",
			currency: "USD",
			minimumFractionDigits: cents ? 2 : 0,
			maximumFractionDigits: cents ? 2 : 0,
		});
	}

	return (
		<Brace $order={order}>
		{ order === "reverse" ? (
				<>
					<Token>{brktValue()}{label ? " brktETH" : ""}</Token>
					<Dollars $font={font}>{dollarValue()}</Dollars>
				</>
			) : (
				<VV>
					<Dollars $font={font}>{dollarValue()}</Dollars>
					{ !hideBracket && ( <Token>{brktValue()}{label ? " brktETH" : ""}</Token> ) }
				</VV>
			)
		}
		</Brace>
	);
};

const VV = styled(FlexStream)`
	align-items: flex-end;
`;

const Brace = styled(Stream)<{ $order?: "reverse" }>`
	white-space: nowrap;
	align-items: center;
	flex-direction: ${({ $order }) => $order === "reverse" ? "row-reverse" : "row"};
	gap: 0.25rem;
  p {
		display: flex;
		gap: 0.25rem;
	}
`;

const Dollars = styled.span<{ $font?: keyof FontPalette; }>`
	display: flex;
	justify-content: center;
	font-family: ${({ $font }) => $font ? getTheme().fonts[$font] : getTheme().fonts.numeric};
	font-size: 1.25rem;
	font-weight: 500;
`;

const Token = styled.span`
	display: flex;
	line-height: 1.6rem;
	justify-content: center;
	font-family: ${() => getTheme().fonts.body};
	font-size: 0.8rem;
	font-weight: 500;
	margin-top: 1px;
	margin-left: 3px;
`;
